import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { EnvResponseModel, EnvironmentModel, StaticEnvironmentModel } from '../models/environment.model';
import { environment } from 'apps/studio/src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EnvService {

  isEnviromentChanged = signal<string | null>(null);
  env!: EnvironmentModel | StaticEnvironmentModel;

  constructor(
    private http: HttpClient,
    @Inject('environment') private environment: EnvironmentModel,
    @Optional() @Inject('REQUEST') private request: any
  ) { 
      this.env = { ...this.environment };
  }

  getEnvironment(): EnvironmentModel | StaticEnvironmentModel {
    return this.env;
  }

  /** 🔹 Step 1: Detect domain or region and trigger env setup */
  public async domainChecking(region: string): Promise<boolean> {
    return await this.setEnvironment({ info: region });
  }


  /** 🔹 Step 2: Decide Local or Remote Environment */
  public async setEnvironment(params?: { info?: string }): Promise<boolean> {
    return this.isRemoteEnvironment()
      ? await this.setRemoteEnvironment(params)
      : this.setLocalEnvironment();
  }

  private isRemoteEnvironment(): boolean {
    const value = (
      this.environment &&
      this.environment.app !== 'local' &&
      this.environment.app !== 'development' &&
      this.environment.app !== 'test' &&
      !!this.environment.backendServerConfigUrl
    );
    console.info('isRemoteEnvironment:', {environmenApp :this.environment.app, backendServerConfigUrl: this.environment.backendServerConfigUrl, value});
    return value;
  }

  /** 🔹 Step 3: Fetch Environment From Backend (if Remote) */
  private async setRemoteEnvironment(params?: { info?: string }): Promise<boolean> {
    try {
      if (!this.environment.backendServerConfigUrl) return false;
      const headers = typeof window === 'undefined'
        ? new HttpHeaders({
          origin: this.request?.protocol + '://' + this.request?.headers?.host,
        })
        : undefined;

      const response = await lastValueFrom(
        this.http.get<EnvResponseModel>(this.environment.backendServerConfigUrl, { params, headers })
      );

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching remote environment:', error);
      return false;
    }
  }

  /** 🔹 Step 4: Handle API Response */
  /** 🔹 Step 4: Handle API Response */
  private handleResponse(response: any): boolean {
    if (response && response.result) {
      const result = response.result;

      // 🧩 Rename existing keys in-place
      result.backendUrl = result.apiUrlDb ?? result.backendUrl;
      result.zenbasketUrl = result.hostUrl ?? result.zenbasketUrl;
      result.zbStorefrontUrl = result.storefrontUrl ?? result.zbStorefrontUrl;
      result.awsUrl = result.AWSImageUrl ?? result.awsUrl;
      result.awsRegion = result.AWSregion ?? result.awsRegion;
      result.mediaBaseUrl = result.mediaBaseUrl;
      result.zbbundleUri = result.graphqlUri ?? result.zbbundleUri;
      result.graphqlUri = result.backendUrl ? `${result.backendUrl.replace(/\/+$/, '')}/studio/graphql` : '';
      result.secretKey = result.secretKey ?? this.environment?.secretKey;
      result.region = result.countryCode ?? this.environment?.region;
      // 🧹 Optionally remove old keys (to avoid confusion)
      delete result.apiUrlDb;
      delete result.hostUrl;
      delete result.storefrontUrl;
      delete result.AWSImageUrl;
      delete result.AWSregion;

      // ✅ Save final env
      this.env = {
        ...this.environment,
        ...result
      };

      // 🔄 Update region signal + storage
      if (
        typeof window !== 'undefined' &&
        localStorage.getItem('region') !== result.countryCode
      ) {
        this.isEnviromentChanged.set(result.countryCode);
        localStorage.setItem('region', result.countryCode);
      }

      return true;
    }

    return false;
  }

  /** 🔹 Step 5: Local Environment Fallback */
  private setLocalEnvironment(): Promise<boolean> {
    return new Promise(resolve => {
      const localEnv = this.getLocalEnvironment();
      this.env = { ...localEnv };
      resolve(true);
    });
  }

  private getLocalEnvironment(): StaticEnvironmentModel {
    return {
      app: environment.app,
      graphqlUri: environment.graphqlUri,
      awsRegion: environment.awsRegion,
      bucketName: environment.bucketName,
      zbbundleUri: environment.zbbundleUri,
      zenbasketUrl: environment.zenbasketUrl,
      secretKey: environment.secretKey,
      awsUrl: environment.awsUrl,
      awsFileUrl: environment.awsFileUrl,
      mediaBaseUrl: environment.mediaBaseUrl,
      backendUrl: environment.backendUrl,
      zbStorefrontUrl: environment.zbStorefrontUrl,
      RegionUrl: environment.region,
      imageMediaBaseUrl: environment.imageMediaBaseUrl,
      videoMediaBaseUrl: environment.videoMediaBaseUrl,
  };
  }
}
